import type { VideoEmbed, VideoProvider } from "./types";

function host(url: URL, ...hosts: string[]) {
  return hosts.includes(url.hostname);
}
function iframe(provider: string, src: string, portrait = false): VideoEmbed {
  return { kind: "iframe", src, provider, portrait };
}
function seconds(value: string | null): number | null {
  if (!value) return null;
  if (/^\d+$/.test(value)) {
    const n = Number(value);
    return Number.isSafeInteger(n) ? n : null;
  }
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(value);
  if (!match) return null;
  const n =
    Number(match[1] ?? 0) * 3600 +
    Number(match[2] ?? 0) * 60 +
    Number(match[3] ?? 0);
  return Number.isSafeInteger(n) ? n : null;
}

export const youtubeProvider: VideoProvider = {
  resolve(url) {
    let id: string | undefined | null;
    if (host(url, "youtu.be", "www.youtu.be"))
      id = /^\/([\w-]{11})\/?$/.exec(url.pathname)?.[1];
    else if (
      host(
        url,
        "youtube.com",
        "www.youtube.com",
        "m.youtube.com",
        "music.youtube.com",
        "youtube-nocookie.com",
        "www.youtube-nocookie.com",
      )
    )
      id =
        url.pathname === "/watch"
          ? url.searchParams.get("v")
          : /^\/(?:embed|shorts|live|v)\/([\w-]{11})\/?$/.exec(
              url.pathname,
            )?.[1];
    if (!id || !/^[\w-]{11}$/.test(id)) return null;
    const embed = new URL(`https://www.youtube-nocookie.com/embed/${id}`);
    const start = seconds(
      url.searchParams.get("start") ??
        url.searchParams.get("t") ??
        new URLSearchParams(url.hash.slice(1)).get("t"),
    );
    if (start) embed.searchParams.set("start", String(start));
    embed.searchParams.set("playsinline", "1");
    return iframe("YouTube", embed.href);
  },
};

export const vimeoProvider: VideoProvider = {
  resolve(url) {
    if (!host(url, "vimeo.com", "www.vimeo.com", "player.vimeo.com"))
      return null;
    const match =
      /^(?:\/video|\/channels\/[^/]+|\/groups\/[^/]+\/videos|\/showcase\/\d+\/video)?\/(\d+)(?:\/([a-f\d]+))?\/?$/i.exec(
        url.pathname,
      );
    if (!match) return null;
    const embed = new URL(`https://player.vimeo.com/video/${match[1]}`);
    const hash = url.searchParams.get("h") ?? match[2];
    if (hash && /^[a-f\d]+$/i.test(hash)) embed.searchParams.set("h", hash);
    return iframe("Vimeo", embed.href);
  },
};

export const dailymotionProvider: VideoProvider = {
  resolve(url, context) {
    let id: string | undefined | null;
    let playerId = context.dailymotionPlayerId;
    if (host(url, "dailymotion.com", "www.dailymotion.com"))
      id = /^\/(?:embed\/)?video\/([a-z\d]+)(?:_[^/]*)?\/?$/i.exec(
        url.pathname,
      )?.[1];
    else if (host(url, "dai.ly"))
      id = /^\/([a-z\d]+)\/?$/i.exec(url.pathname)?.[1];
    else if (host(url, "geo.dailymotion.com")) {
      playerId = /^\/player\/([a-z\d]+)\.html$/i.exec(url.pathname)?.[1];
      id = url.searchParams.get("video");
    }
    // Dailymotion requires a configured Player ID for its current endpoint.
    if (
      !id ||
      !/^[a-z\d]+$/i.test(id) ||
      !playerId ||
      !/^[a-z\d]+$/i.test(playerId)
    )
      return null;
    return iframe(
      "Dailymotion",
      `https://geo.dailymotion.com/player/${playerId}.html?video=${id}`,
    );
  },
};

export const twitchProvider: VideoProvider = {
  resolve(url, context) {
    const parent = context.hostname;
    if (!parent || !/^[a-z\d.-]+$/i.test(parent)) return null;
    let embed: URL;
    if (host(url, "clips.twitch.tv")) {
      const clip =
        url.pathname === "/embed"
          ? url.searchParams.get("clip")
          : /^\/([\w-]+)\/?$/.exec(url.pathname)?.[1];
      if (!clip || !/^[\w-]+$/.test(clip)) return null;
      embed = new URL("https://clips.twitch.tv/embed");
      embed.searchParams.set("clip", clip);
    } else if (
      host(url, "twitch.tv", "www.twitch.tv", "m.twitch.tv", "player.twitch.tv")
    ) {
      const clip = /^\/\w+\/clip\/([\w-]+)\/?$/.exec(url.pathname)?.[1];
      if (clip) {
        embed = new URL("https://clips.twitch.tv/embed");
        embed.searchParams.set("clip", clip);
      } else {
        const video =
          /^\/videos\/(\d+)\/?$/.exec(url.pathname)?.[1] ??
          url.searchParams.get("video")?.replace(/^v/, "");
        const channel =
          /^\/([\w]+)\/?$/.exec(url.pathname)?.[1] ??
          url.searchParams.get("channel");
        embed = new URL("https://player.twitch.tv/");
        if (video && /^\d+$/.test(video))
          embed.searchParams.set("video", `v${video}`);
        else if (
          channel &&
          /^\w+$/.test(channel) &&
          !["directory", "downloads", "settings", "videos"].includes(channel)
        )
          embed.searchParams.set("channel", channel);
        else return null;
        const start = url.searchParams.get("t") ?? url.searchParams.get("time");
        if (start && seconds(start) !== null)
          embed.searchParams.set("time", `${seconds(start)}s`);
      }
    } else return null;
    embed.searchParams.set("parent", parent);
    embed.searchParams.set("autoplay", "false");
    return iframe("Twitch", embed.href);
  },
};

export const tiktokProvider: VideoProvider = {
  resolve(url) {
    if (!host(url, "tiktok.com", "www.tiktok.com", "m.tiktok.com")) return null;
    const id = /^\/(?:@[^/]+\/video|player\/v1|embed\/v2)\/(\d+)\/?$/.exec(
      url.pathname,
    )?.[1];
    return id
      ? iframe(
          "TikTok",
          `https://www.tiktok.com/player/v1/${id}?autoplay=0`,
          true,
        )
      : null;
  },
};

export const facebookProvider: VideoProvider = {
  resolve(url) {
    if (!host(url, "facebook.com", "www.facebook.com", "m.facebook.com"))
      return null;
    let video = url;
    if (url.pathname === "/plugins/video.php") {
      try {
        video = new URL(url.searchParams.get("href") ?? "");
      } catch {
        return null;
      }
    }
    if (
      video.protocol !== "https:" ||
      video.username ||
      video.password ||
      video.port ||
      !host(video, "facebook.com", "www.facebook.com", "m.facebook.com")
    )
      return null;
    if (
      !(
        /^\/(?:[^/]+\/videos\/(?:[^/]+\/)?\d+|reel\/\d+)\/?$/.test(
          video.pathname,
        ) ||
        (/^\/watch\/?$/.test(video.pathname) &&
          /^\d+$/.test(video.searchParams.get("v") ?? ""))
      )
    )
      return null;
    const embed = new URL("https://www.facebook.com/plugins/video.php");
    embed.searchParams.set("href", video.href);
    embed.searchParams.set("show_text", "false");
    embed.searchParams.set("autoplay", "false");
    return iframe("Facebook", embed.href);
  },
};

export const instagramProvider: VideoProvider = {
  resolve(url) {
    if (!host(url, "instagram.com", "www.instagram.com")) return null;
    const match = /^\/(p|reel|reels|tv)\/([\w-]+)(?:\/embed)?\/?$/.exec(
      url.pathname,
    );
    if (!match) return null;
    const kind = match[1] === "reels" ? "reel" : match[1];
    return iframe(
      "Instagram",
      `https://www.instagram.com/${kind}/${match[2]}/embed/`,
      true,
    );
  },
};

export const niconicoProvider: VideoProvider = {
  resolve(url) {
    let id: string | undefined;
    if (host(url, "nicovideo.jp", "www.nicovideo.jp", "sp.nicovideo.jp"))
      id = /^\/watch\/((?:sm|so|nm)\d+|\d+)\/?$/.exec(url.pathname)?.[1];
    else if (host(url, "nico.ms"))
      id = /^\/((?:sm|so|nm)\d+|\d+)\/?$/.exec(url.pathname)?.[1];
    else if (host(url, "embed.nicovideo.jp"))
      id = /^\/watch\/((?:sm|so|nm)\d+|\d+)\/?$/.exec(url.pathname)?.[1];
    return id
      ? iframe(
          "ニコニコ動画",
          `https://embed.nicovideo.jp/watch/${id}?autoplay=0`,
        )
      : null;
  },
};

export const bilibiliProvider: VideoProvider = {
  resolve(url) {
    let bvid: string | undefined | null;
    let aid: string | undefined | null;
    if (host(url, "bilibili.com", "www.bilibili.com", "m.bilibili.com")) {
      bvid = /^\/video\/(BV[a-z\d]{10})\/?$/i.exec(url.pathname)?.[1];
      aid = /^\/video\/av(\d+)\/?$/i.exec(url.pathname)?.[1];
    } else if (
      host(url, "player.bilibili.com") &&
      url.pathname === "/player.html"
    ) {
      bvid = url.searchParams.get("bvid");
      aid = url.searchParams.get("aid");
    }
    const embed = new URL("https://player.bilibili.com/player.html");
    if (bvid && /^BV[a-z\d]{10}$/i.test(bvid))
      embed.searchParams.set("bvid", bvid);
    else if (aid && /^\d+$/.test(aid)) embed.searchParams.set("aid", aid);
    else return null;
    const page = url.searchParams.get("p") ?? url.searchParams.get("page");
    if (page && /^[1-9]\d{0,4}$/.test(page)) embed.searchParams.set("p", page);
    const start = seconds(url.searchParams.get("t"));
    if (start) embed.searchParams.set("t", String(start));
    embed.searchParams.set("autoplay", "0");
    return iframe("bilibili", embed.href);
  },
};

export const directVideoProvider: VideoProvider = {
  resolve(url) {
    return /\.(mp4|webm|ogv|ogg)$/i.test(url.pathname)
      ? { kind: "video", src: url.href, provider: "動画" }
      : null;
  },
};
